import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { form, FormField as SignalFormField } from '@angular/forms/signals';
import {
  SignalColorField,
  SignalDropdown,
  SignalTextarea,
  type TriggerActivatedEvent,
} from '@vallift/ngl';
import {
  BoundTextSlashMenuComponent,
  type SlashMenuAnchorRect,
  type SlashMenuItem,
} from '../../../bound-text-slash-menu/bound-text-slash-menu';
import { formatBoundTextToken } from '../../../../utils/bound-text-template';
import { DEFAULT_TEXT_FONT_FAMILY, TEXT_FONT_FAMILY_OPTIONS } from '../../shared/typography-config.constants';
import { TypographyConfigControlsComponent } from '../../shared/typography-config-controls/typography-config-controls';
import type {
  FormField,
  HeadingAlignment,
  HeadingFontFamily,
  HeadingFontStyle,
  HeadingFontWeight,
  HeadingLevel,
} from '../../../../models/built-form';

interface SelectOption<T> {
  label: string;
  value: T;
}

interface HeadingConfigModel {
  label: string;
  headingLevel: HeadingLevel;
  headingAlignment: HeadingAlignment;
  headingFontStyle: HeadingFontStyle;
  headingFontFamily: HeadingFontFamily;
  headingColor: string;
  headingFontWeight: HeadingFontWeight;
}

@Component({
  selector: 'app-heading-config-view',
  standalone: true,
  imports: [
    SignalFormField,
    SignalTextarea,
    SignalDropdown,
    SignalColorField,
    TypographyConfigControlsComponent,
    BoundTextSlashMenuComponent,
  ],
  templateUrl: './heading-config-view.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeadingConfigViewComponent {
  private readonly labelField = viewChild(SignalTextarea);

  readonly field = input.required<FormField>();
  readonly fieldChange = output<FormField>();

  readonly headingLevelOptions: SelectOption<HeadingLevel>[] = [
    { label: 'Heading 2', value: 2 },
    { label: 'Heading 3', value: 3 },
    { label: 'Heading 4', value: 4 },
    { label: 'Heading 5', value: 5 },
  ];

  readonly fontFamilyOptions = TEXT_FONT_FAMILY_OPTIONS;

  readonly model = signal<HeadingConfigModel>({
    label: '',
    headingLevel: 2,
    headingAlignment: 'left',
    headingFontStyle: 'normal',
    headingFontFamily: DEFAULT_TEXT_FONT_FAMILY,
    headingColor: '#0f172a',
    headingFontWeight: 400,
  });
  readonly configForm = form(this.model);

  readonly slashMenuOpen = signal(false);
  readonly slashQuery = signal('');
  readonly slashAnchor = signal<SlashMenuAnchorRect | null>(null);

  private lastFieldId: string | null = null;
  private suppressEmit = false;
  private pending: TriggerActivatedEvent | null = null;

  constructor() {
    effect(() => {
      const next = this.field();
      if (next.id === this.lastFieldId) {
        return;
      }
      this.lastFieldId = next.id;
      this.suppressEmit = true;
      this.closeSlashMenu();
      this.model.set({
        label: next.label,
        headingLevel: next.headingLevel ?? 2,
        headingAlignment: next.headingAlignment ?? 'left',
        headingFontStyle: next.headingFontStyle ?? 'normal',
        headingFontFamily: next.headingFontFamily ?? DEFAULT_TEXT_FONT_FAMILY,
        headingColor: next.headingColor ?? '#000000',
        headingFontWeight: next.headingFontWeight ?? 400,
      });
    });

    effect(() => {
      const draft = this.model();
      if (this.suppressEmit) {
        this.suppressEmit = false;
        return;
      }
      const field = untracked(() => this.field());
      if (!field || field.id !== this.lastFieldId) {
        return;
      }
      this.fieldChange.emit({
        ...field,
        label: draft.label,
        headingLevel: draft.headingLevel,
        headingAlignment: draft.headingAlignment,
        headingFontStyle: draft.headingFontStyle,
        headingFontFamily: draft.headingFontFamily,
        headingColor: draft.headingColor,
        headingFontWeight: draft.headingFontWeight,
        placeholder: undefined,
        helpText: undefined,
        required: undefined,
        validation: undefined,
        options: undefined,
        spacerSize: undefined,
      });
    });
  }

  setLabel(label: string): void {
    this.model.update((model) => ({ ...model, label }));
    this.syncSlashMenuFromValue(label);
  }

  onTriggerActivated(event: TriggerActivatedEvent): void {
    this.pending = event;
    this.slashQuery.set(event.query ?? '');
    this.slashAnchor.set(this.rectFromTarget(this.visibleAnchor(event.target)));
    this.slashMenuOpen.set(true);
  }

  onSlashSelect(item: SlashMenuItem): void {
    if (!this.pending) {
      this.closeSlashMenu();
      return;
    }
    const event = this.pending;
    const token = formatBoundTextToken(item.namespace, item.key, item.title);
    const triggerLen = event.trigger.length + (event.query?.length ?? 0);
    const start = Math.max(0, event.caretIndex - triggerLen);
    const current = this.model().label;
    const next =
      current.slice(0, start) + token + current.slice(event.caretIndex);
    const caret = start + token.length;
    this.closeSlashMenu();
    this.setLabel(next);
    queueMicrotask(() => this.labelField()?.focusHandlebars(caret));
  }

  onSlashDismiss(): void {
    this.closeSlashMenu();
  }

  setFontWeight(weight: HeadingFontWeight): void {
    this.model.update((model) => ({
      ...model,
      headingFontWeight: weight,
    }));
  }

  setAlignment(value: HeadingAlignment): void {
    this.model.update((current) => ({ ...current, headingAlignment: value }));
  }

  setFontStyle(value: HeadingFontStyle): void {
    this.model.update((current) => ({ ...current, headingFontStyle: value }));
  }

  private syncSlashMenuFromValue(label: string): void {
    if (!this.slashMenuOpen() || !this.pending) {
      return;
    }
    // NGL allows `/` without a leading space; track the active slash span loosely.
    const trigger = this.pending.trigger || '/';
    const slashIndex = label.lastIndexOf(trigger);
    if (slashIndex < 0) {
      this.closeSlashMenu();
      return;
    }
    const query = label.slice(slashIndex + trigger.length);
    if (/[\n\r{]/.test(query)) {
      this.closeSlashMenu();
      return;
    }
    this.pending = {
      ...this.pending,
      query,
      caretIndex: slashIndex + trigger.length + query.length,
    };
    this.slashQuery.set(query);
  }

  private closeSlashMenu(): void {
    this.slashMenuOpen.set(false);
    this.slashQuery.set('');
    this.slashAnchor.set(null);
    this.pending = null;
  }

  private visibleAnchor(
    target: HTMLInputElement | HTMLTextAreaElement | HTMLElement,
  ): HTMLElement {
    const wrapped = (target as HTMLElement).closest?.(
      '[data-bound-text-slash-anchor]',
    );
    const visible = wrapped?.querySelector<HTMLElement>(
      '[contenteditable="true"], textarea:not([aria-hidden="true"]), input:not([aria-hidden="true"])',
    );
    return visible ?? (target as HTMLElement);
  }

  private rectFromTarget(target: HTMLElement): SlashMenuAnchorRect {
    const rect = target.getBoundingClientRect();
    return {
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
      width: rect.width,
      height: rect.height,
    };
  }
}
